/**
 * F4 — Project a PortableSite into a SharePoint-uploadable tree.
 *
 * F7 (vault export → new SP site) consumes this — it produces the
 * per-file payloads (path, body, MIME) the provisioner uploads, plus
 * the per-file custom-column hints. Pure module; no Graph deps.
 *
 * Conversion rules:
 *   - Wikilinks `[[Foo/Bar]]` rewrite to relative paths `./Foo/Bar.md`
 *     so a user clicking through SharePoint Online lands on the right
 *     `.md` file. Aliases (`[[Foo|alias]]`) become `[alias](./Foo.md)`.
 *   - Embeds `![[image.png]]` rewrite to `![alt](./image.png)`.
 *   - YAML frontmatter is re-serialized verbatim above the body.
 *   - Tags survive in two places: as `tags:` in the YAML AND as a
 *     `tagsCustomColumn` hint the caller can write to a SP list
 *     column. SP can index either source.
 */

import { stringify as stringifyYaml } from 'yaml';
import type { PortableSite } from './types';

export interface SharePointFile {
  /** Drive-relative path including extension. */
  path: string;
  /** Lowercased extension without the dot. */
  ext: string;
  /** UTF-8 body (markdown for notes, JSON for flows). */
  body: string;
  /** Custom column hints. SP list-item PATCH payload (when writing). */
  columns: {
    tags?: string[];
    callouts?: string[];
    [extra: string]: unknown;
  };
  /** Optional originating note path (for debugging). */
  origin: string;
}

export interface SharePointAsset {
  path: string;
  ext: string;
  bytes: Uint8Array;
  mimeType?: string;
}

export interface SharePointTree {
  files: SharePointFile[];
  assets: SharePointAsset[];
}

const WIKILINK_REGEX = /\[\[([^\]|#\n]+?)(?:#([^\]|\n]+))?(?:\|([^\]\n]+))?\]\]/g;
const EMBED_REGEX = /!\[\[([^\]\n]+?)\]\]/g;

export function toSharePointTree(site: PortableSite): SharePointTree {
  const noteFiles: SharePointFile[] = site.notes.map((n) => {
    const rewrittenBody = rewriteBodyLinks(n.body, n.path);
    const yaml = Object.keys(n.frontmatter ?? {}).length > 0
      ? `---\n${stringifyYaml(n.frontmatter).trimEnd()}\n---\n\n`
      : '';
    return {
      path: n.path,
      ext: 'md',
      body: `${yaml}${rewrittenBody}`,
      columns: {
        tags: n.tags,
        callouts: n.callouts,
      },
      origin: n.path,
    };
  });
  const flowFiles: SharePointFile[] = site.flows.map((f) => ({
    path: f.path,
    ext: 'flow',
    body: f.json,
    columns: {},
    origin: f.path,
  }));
  const assets: SharePointAsset[] = site.assets.map((a) => ({
    path: a.path,
    ext: a.ext,
    bytes: a.bytes,
    ...(a.mimeType !== undefined ? { mimeType: a.mimeType } : {}),
  }));
  return {
    files: [...noteFiles, ...flowFiles],
    assets,
  };
}

/**
 * Rewrite wikilinks + embeds inside a single note body to SharePoint
 * relative-path form. Exported for direct unit testing — round-tripping
 * requires re-extracting wikilinks on the way back, which the import
 * side already does.
 */
export function rewriteBodyLinks(body: string, originPath: string): string {
  const originDir = originPath.includes('/')
    ? originPath.slice(0, originPath.lastIndexOf('/'))
    : '';
  let out = body.replace(EMBED_REGEX, (_match, target: string) => {
    const trimmed = target.trim();
    const rel = relativePathFromOrigin(originDir, trimmed);
    return `![](${rel})`;
  });
  out = out.replace(WIKILINK_REGEX, (_match, target: string, _section: string | undefined, alias: string | undefined) => {
    const trimmed = target.trim();
    const targetPath = trimmed.endsWith('.md') ? trimmed : `${trimmed}.md`;
    const rel = relativePathFromOrigin(originDir, targetPath);
    const label = alias ? alias.trim() : trimmed;
    return `[${label}](${rel})`;
  });
  return out;
}

function relativePathFromOrigin(originDir: string, targetPath: string): string {
  if (!originDir) return `./${targetPath}`;
  // Identical directory → ./{name}
  const originParts = originDir.split('/').filter(Boolean);
  const targetParts = targetPath.split('/').filter(Boolean);
  // Find the common prefix.
  let common = 0;
  while (
    common < originParts.length &&
    common < targetParts.length - 1 &&
    originParts[common] === targetParts[common]
  ) {
    common++;
  }
  const up = originParts.length - common;
  const down = targetParts.slice(common);
  if (up === 0) return `./${down.join('/')}`;
  return `${'../'.repeat(up)}${down.join('/')}`;
}
